import { CodeBlock } from '../../components/developers/CodeBlock';
import { MermaidDiagram } from '../../components/developers/MermaidDiagram';

export default function BundleCompilation() {
  const bundleFlowDiagram = `graph LR
    A[Policy Files] --> B[Validate]
    B --> C[Compile]
    C --> D[Optimize]
    D --> E[Package]
    E --> F[Sign]
    F --> G[Bundle.tar.gz]`;

  return (
    <article className="prose prose-slate max-w-none">
      <h1>Bundle Compilation</h1>
      <p className="lead">
        Compile and package your policies into deployable bundles for production use.
      </p>

      <h2>What is a Bundle?</h2>
      <p>
        A bundle is a compiled, optimized, and signed package containing one or more policies ready
        for deployment. Bundles ensure consistent policy execution across environments.
      </p>

      <MermaidDiagram chart={bundleFlowDiagram} caption="Bundle Compilation Pipeline" />

      <h2>Project Structure</h2>
      <p>Organize your policy project:</p>

      <CodeBlock
        language="bash"
        code={`my-policies/
 opair.config.yaml      # Project configuration
 policies/
    content-mod.opair  # Policy definitions
    rate-limit.opair
    auth-rules.opair
 data/
    blocklist.json     # Static data files
    allowlist.json
 tests/
    test-cases.yaml    # Test definitions
 dist/                   # Compiled output
     bundle.tar.gz`}
      />

      <h2>Configuration</h2>
      <p>Define your bundle configuration:</p>

      <CodeBlock
        title="opair.config.yaml"
        language="yaml"
        code={`bundle:
  name: my-policies
  version: 1.0.0
  description: Production policy bundle

compile:
  input: ./policies
  output: ./dist
  optimization: true
  minify: true

signing:
  enabled: true
  key: \${OPAIR_SIGNING_KEY}

data:
  include:
    - ./data/*.json

metadata:
  author: Platform Team
  environment: production`}
      />

      <h2>Compilation</h2>

      <h3>Basic Compilation</h3>
      <CodeBlock
        language="bash"
        code={`# Compile all policies
opair bundle compile

# Compile with custom output
opair bundle compile --output my-bundle.tar.gz

# Compile specific policies
opair bundle compile --include "content-*"`}
      />

      <h3>Validation</h3>
      <CodeBlock
        language="bash"
        code={`# Validate before compilation
opair bundle validate

# Validate specific policy
opair bundle validate policies/content-mod.opair

# Strict validation mode
opair bundle validate --strict`}
      />

      <h3>Optimization</h3>
      <p>Enable optimizations for production:</p>
      <CodeBlock
        language="bash"
        code={`# Full optimization
opair bundle compile --optimize

# Optimization options
opair bundle compile \\
  --optimize \\
  --minify \\
  --tree-shake \\
  --dedupe`}
      />

      <h2>Bundle Contents</h2>
      <p>Inspect what is inside a bundle:</p>
      <CodeBlock
        language="bash"
        code={`opair bundle inspect bundle.tar.gz

# Output:
# Bundle: my-policies v1.0.0
# Policies: 3
# Data files: 2
# Size: 45.2 KB
# Signature: Valid
# 
# Contents:
#   - content-mod (v1.0.0)
#   - rate-limit (v1.2.0)
#   - auth-rules (v1.0.0)`}
      />

      <h2>Distribution</h2>

      <h3>Push to Registry</h3>
      <CodeBlock
        language="bash"
        code={`# Push bundle to registry
opair bundle push bundle.tar.gz --tag v1.0.0

# Push with metadata
opair bundle push bundle.tar.gz \\
  --tag v1.0.0 \\
  --tag latest \\
  --description "Production release"`}
      />

      <h3>Pull from Registry</h3>
      <CodeBlock
        language="bash"
        code={`# Pull specific version
opair bundle pull my-policies:v1.0.0

# Pull latest
opair bundle pull my-policies:latest

# Pull and verify signature
opair bundle pull my-policies:v1.0.0 --verify`}
      />

      <h2>CI/CD Integration</h2>
      <p>Example GitHub Actions workflow:</p>

      <CodeBlock
        title=".github/workflows/bundle.yml"
        language="yaml"
        code={`name: Build and Deploy Bundle

on:
  push:
    tags:
      - 'v*'

jobs:
  build:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      
      - name: Install Opair CLI
        run: npm install -g @opair/cli
      
      - name: Validate policies
        run: opair bundle validate --strict
      
      - name: Run tests
        run: opair test ./policies
      
      - name: Build bundle
        run: opair bundle compile --optimize
        env:
          OPAIR_SIGNING_KEY: \${{ secrets.SIGNING_KEY }}
      
      - name: Push to registry
        run: opair bundle push dist/bundle.tar.gz --tag \${{ github.ref_name }}
        env:
          OPAIR_API_KEY: \${{ secrets.API_KEY }}`}
      />

      <div className="not-prose my-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <h3 className="text-lg font-semibold text-amber-900 mb-2">Deployment Tips</h3>
        <ul className="text-amber-800 space-y-1 text-sm">
          <li>Always run tests before deploying to production</li>
          <li>Use semantic versioning for bundle releases</li>
          <li>Sign bundles to ensure integrity</li>
          <li>Keep bundles small by excluding unnecessary data</li>
          <li>Use immutable tags for production deployments</li>
        </ul>
      </div>
    </article>
  );
}
